import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { Progress } from './components/ui/progress';
import { Alert, AlertDescription } from './components/ui/alert';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { User, FileText, Building, DollarSign, Upload, CheckCircle, AlertCircle } from 'lucide-react';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [authMode, setAuthMode] = useState('login');
  const [showLoanForm, setShowLoanForm] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    personal_info: {},
    loan_details: {},
    employment_info: {},
    financial_info: {},
    documents: {}
  });
  const [userApplications, setUserApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      fetchCurrentUser(token);
    }
  }, []);

  const fetchCurrentUser = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const user = await response.json();
        setCurrentUser(user);
        fetchUserApplications(token);
      } else {
        localStorage.removeItem('authToken');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      localStorage.removeItem('authToken');
    }
  };

  const fetchUserApplications = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/loans/applications`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setUserApplications(data.applications);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const formData = new FormData(e.target);
    const email = formData.get('email');
    const password = formData.get('password');
    const full_name = formData.get('full_name');

    try {
      const endpoint = authMode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const body = authMode === 'login' 
        ? { email, password }
        : { email, password, full_name };

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('authToken', data.access_token);
        await fetchCurrentUser(data.access_token);
        setSuccess(authMode === 'login' ? 'Login successful!' : 'Registration successful!');
      } else {
        setError(data.detail || 'Authentication failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setCurrentUser(null);
    setShowLoanForm(false);
    setCurrentStep(1);
    setFormData({
      personal_info: {},
      loan_details: {},
      employment_info: {},
      financial_info: {},
      documents: {}
    });
  };

  const updateFormData = (section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], ...data }
    }));
  };

  const handleStepSubmit = (stepData) => {
    const sections = ['personal_info', 'loan_details', 'employment_info', 'financial_info', 'documents'];
    updateFormData(sections[currentStep - 1], stepData);
    
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    } else {
      submitLoanApplication();
    }
  };

  const submitLoanApplication = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/loans/applications`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Loan application submitted successfully!');
        setShowLoanForm(false);
        setCurrentStep(1);
        setFormData({
          personal_info: {},
          loan_details: {},
          employment_info: {},
          financial_info: {},
          documents: {}
        });
        fetchUserApplications(token);
      } else {
        setError(data.detail || 'Failed to submit application');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Authentication Form Component
  const AuthForm = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="backdrop-blur-lg bg-white/80 border-0 shadow-2xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-gray-800">
              {authMode === 'login' ? 'Welcome Back' : 'Create Account'}
            </CardTitle>
            <CardDescription>
              {authMode === 'login' ? 'Sign in to your loan account' : 'Get started with your loan application'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert className="mb-4 border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700">{error}</AlertDescription>
              </Alert>
            )}
            {success && (
              <Alert className="mb-4 border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-700">{success}</AlertDescription>
              </Alert>
            )}
            
            <form onSubmit={handleAuth} className="space-y-4">
              {authMode === 'register' && (
                <div>
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    type="text"
                    required
                    className="mt-1"
                    placeholder="Enter your full name"
                  />
                </div>
              )}
              
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="mt-1"
                  placeholder="Enter your email"
                />
              </div>
              
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="mt-1"
                  placeholder="Enter your password"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700 transition-all duration-200"
                disabled={loading}
              >
                {loading ? 'Processing...' : authMode === 'login' ? 'Sign In' : 'Create Account'}
              </Button>
            </form>
            
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
                className="text-blue-600 hover:text-blue-700 underline transition-colors"
              >
                {authMode === 'login' ? 'Need an account? Sign up' : 'Already have an account? Sign in'}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // Step Components
  const PersonalInfoStep = () => {
    const [stepData, setStepData] = useState(formData.personal_info);
    const [validationErrors, setValidationErrors] = useState({});

    const validateStep = () => {
      const errors = {};
      const requiredFields = [
        'full_name', 'email', 'phone', 'date_of_birth', 
        'address', 'city', 'state', 'zip_code', 'marital_status'
      ];

      requiredFields.forEach(field => {
        if (!stepData[field] || stepData[field].trim() === '') {
          errors[field] = 'This field is required';
        }
      });

      // Email validation
      if (stepData.email && !/\S+@\S+\.\S+/.test(stepData.email)) {
        errors.email = 'Please enter a valid email address';
      }

      // Phone validation (basic)
      if (stepData.phone && !/^\d{10,15}$/.test(stepData.phone.replace(/[^\d]/g, ''))) {
        errors.phone = 'Please enter a valid phone number';
      }

      // ZIP code validation
      if (stepData.zip_code && !/^\d{5}(-\d{4})?$/.test(stepData.zip_code)) {
        errors.zip_code = 'Please enter a valid ZIP code';
      }

      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const handleNext = () => {
      if (validateStep()) {
        handleStepSubmit(stepData);
      } else {
        setError('Please fill in all required fields correctly');
      }
    };

    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Personal Information
          </CardTitle>
          <CardDescription>Please provide your basic personal details. Fields marked with * are required.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="full_name" className="flex items-center gap-1">
                Full Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="full_name"
                value={stepData.full_name || ''}
                onChange={(e) => setStepData({...stepData, full_name: e.target.value})}
                placeholder="Enter your full name"
                className={validationErrors.full_name ? 'border-red-500 focus:border-red-500' : ''}
                required
              />
              {validationErrors.full_name && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.full_name}</p>
              )}
            </div>
            <div>
              <Label htmlFor="email" className="flex items-center gap-1">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={stepData.email || ''}
                onChange={(e) => setStepData({...stepData, email: e.target.value})}
                placeholder="Enter your email"
                className={validationErrors.email ? 'border-red-500 focus:border-red-500' : ''}
                required
              />
              {validationErrors.email && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
              )}
            </div>
            <div>
              <Label htmlFor="phone" className="flex items-center gap-1">
                Phone Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                value={stepData.phone || ''}
                onChange={(e) => setStepData({...stepData, phone: e.target.value})}
                placeholder="Enter your phone number"
                className={validationErrors.phone ? 'border-red-500 focus:border-red-500' : ''}
                required
              />
              {validationErrors.phone && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.phone}</p>
              )}
            </div>
            <div>
              <Label htmlFor="date_of_birth" className="flex items-center gap-1">
                Date of Birth <span className="text-red-500">*</span>
              </Label>
              <Input
                id="date_of_birth"
                type="date"
                value={stepData.date_of_birth || ''}
                onChange={(e) => setStepData({...stepData, date_of_birth: e.target.value})}
                className={validationErrors.date_of_birth ? 'border-red-500 focus:border-red-500' : ''}
                required
              />
              {validationErrors.date_of_birth && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.date_of_birth}</p>
              )}
            </div>
          </div>
          
          <div>
            <Label htmlFor="address" className="flex items-center gap-1">
              Address <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="address"
              value={stepData.address || ''}
              onChange={(e) => setStepData({...stepData, address: e.target.value})}
              placeholder="Enter your full address"
              className={validationErrors.address ? 'border-red-500 focus:border-red-500' : ''}
              required
            />
            {validationErrors.address && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.address}</p>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="city" className="flex items-center gap-1">
                City <span className="text-red-500">*</span>
              </Label>
              <Input
                id="city"
                value={stepData.city || ''}
                onChange={(e) => setStepData({...stepData, city: e.target.value})}
                placeholder="Enter your city"
                className={validationErrors.city ? 'border-red-500 focus:border-red-500' : ''}
                required
              />
              {validationErrors.city && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.city}</p>
              )}
            </div>
            <div>
              <Label htmlFor="state" className="flex items-center gap-1">
                State <span className="text-red-500">*</span>
              </Label>
              <Input
                id="state"
                value={stepData.state || ''}
                onChange={(e) => setStepData({...stepData, state: e.target.value})}
                placeholder="Enter your state"
                className={validationErrors.state ? 'border-red-500 focus:border-red-500' : ''}
                required
              />
              {validationErrors.state && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.state}</p>
              )}
            </div>
            <div>
              <Label htmlFor="zip_code" className="flex items-center gap-1">
                ZIP Code <span className="text-red-500">*</span>
              </Label>
              <Input
                id="zip_code"
                value={stepData.zip_code || ''}
                onChange={(e) => setStepData({...stepData, zip_code: e.target.value})}
                placeholder="Enter ZIP code"
                className={validationErrors.zip_code ? 'border-red-500 focus:border-red-500' : ''}
                required
              />
              {validationErrors.zip_code && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.zip_code}</p>
              )}
            </div>
          </div>
          
          <div>
            <Label htmlFor="marital_status" className="flex items-center gap-1">
              Marital Status <span className="text-red-500">*</span>
            </Label>
            <Select 
              value={stepData.marital_status || ''} 
              onValueChange={(value) => setStepData({...stepData, marital_status: value})}
            >
              <SelectTrigger className={validationErrors.marital_status ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select marital status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single">Single</SelectItem>
                <SelectItem value="married">Married</SelectItem>
                <SelectItem value="divorced">Divorced</SelectItem>
                <SelectItem value="widowed">Widowed</SelectItem>
              </SelectContent>
            </Select>
            {validationErrors.marital_status && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.marital_status}</p>
            )}
          </div>
          
          <Button 
            onClick={handleNext}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            Next Step
          </Button>
        </CardContent>
      </Card>
    );
  };

  const LoanDetailsStep = () => {
    const [stepData, setStepData] = useState(formData.loan_details);
    const [validationErrors, setValidationErrors] = useState({});

    const validateStep = () => {
      const errors = {};
      const requiredFields = [
        'loan_type', 'loan_amount', 'loan_tenure', 'loan_purpose', 'interest_rate_preference'
      ];

      requiredFields.forEach(field => {
        if (!stepData[field] || (typeof stepData[field] === 'string' && stepData[field].trim() === '')) {
          errors[field] = 'This field is required';
        }
      });

      // Loan amount validation
      if (stepData.loan_amount && (stepData.loan_amount <= 0 || stepData.loan_amount > 10000000)) {
        errors.loan_amount = 'Loan amount must be between $1 and $10,000,000';
      }

      // Loan tenure validation
      if (stepData.loan_tenure && (stepData.loan_tenure < 1 || stepData.loan_tenure > 360)) {
        errors.loan_tenure = 'Loan tenure must be between 1 and 360 months';
      }

      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const handleNext = () => {
      if (validateStep()) {
        handleStepSubmit(stepData);
      } else {
        setError('Please fill in all required fields correctly');
      }
    };

    const handlePrevious = () => {
      setCurrentStep(1);
      setError('');
    };

    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Loan Details
          </CardTitle>
          <CardDescription>Specify your loan requirements. Fields marked with * are required.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="loan_type" className="flex items-center gap-1">
              Loan Type <span className="text-red-500">*</span>
            </Label>
            <Select 
              value={stepData.loan_type || ''} 
              onValueChange={(value) => setStepData({...stepData, loan_type: value})}
            >
              <SelectTrigger className={validationErrors.loan_type ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select loan type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personal">Personal Loan</SelectItem>
                <SelectItem value="home">Home Loan</SelectItem>
                <SelectItem value="car">Car Loan</SelectItem>
                <SelectItem value="business">Business Loan</SelectItem>
                <SelectItem value="education">Education Loan</SelectItem>
                <SelectItem value="medical">Medical Loan</SelectItem>
              </SelectContent>
            </Select>
            {validationErrors.loan_type && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.loan_type}</p>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="loan_amount" className="flex items-center gap-1">
                Loan Amount ($) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="loan_amount"
                type="number"
                value={stepData.loan_amount || ''}
                onChange={(e) => setStepData({...stepData, loan_amount: parseFloat(e.target.value)})}
                placeholder="Enter loan amount"
                className={validationErrors.loan_amount ? 'border-red-500 focus:border-red-500' : ''}
                min="1"
                max="10000000"
                required
              />
              {validationErrors.loan_amount && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.loan_amount}</p>
              )}
            </div>
            <div>
              <Label htmlFor="loan_tenure" className="flex items-center gap-1">
                Loan Tenure (months) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="loan_tenure"
                type="number"
                value={stepData.loan_tenure || ''}
                onChange={(e) => setStepData({...stepData, loan_tenure: parseInt(e.target.value)})}
                placeholder="Enter tenure in months"
                className={validationErrors.loan_tenure ? 'border-red-500 focus:border-red-500' : ''}
                min="1"
                max="360"
                required
              />
              {validationErrors.loan_tenure && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.loan_tenure}</p>
              )}
            </div>
          </div>
          
          <div>
            <Label htmlFor="loan_purpose" className="flex items-center gap-1">
              Loan Purpose <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="loan_purpose"
              value={stepData.loan_purpose || ''}
              onChange={(e) => setStepData({...stepData, loan_purpose: e.target.value})}
              placeholder="Describe the purpose of this loan"
              className={validationErrors.loan_purpose ? 'border-red-500 focus:border-red-500' : ''}
              required
            />
            {validationErrors.loan_purpose && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.loan_purpose}</p>
            )}
          </div>
          
        
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handlePrevious}
              className="flex-1"
            >
              Previous
            </Button>
            <Button 
              onClick={handleNext}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              Next Step
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const EmploymentInfoStep = () => {
    const [stepData, setStepData] = useState(formData.employment_info);
    const [validationErrors, setValidationErrors] = useState({});

    const validateStep = () => {
      const errors = {};
      const requiredFields = [
        'employment_type', 'employer_name', 'job_title', 
        'work_experience', 'monthly_income', 'office_address'
      ];

      requiredFields.forEach(field => {
        if (!stepData[field] || (typeof stepData[field] === 'string' && stepData[field].trim() === '')) {
          errors[field] = 'This field is required';
        }
      });

      // Work experience validation
      if (stepData.work_experience && (stepData.work_experience < 0 || stepData.work_experience > 50)) {
        errors.work_experience = 'Work experience must be between 0 and 50 years';
      }

      // Monthly income validation
      if (stepData.monthly_income && (stepData.monthly_income <= 0 || stepData.monthly_income > 1000000)) {
        errors.monthly_income = 'Monthly income must be between $1 and $1,000,000';
      }

      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const handleNext = () => {
      if (validateStep()) {
        handleStepSubmit(stepData);
      } else {
        setError('Please fill in all required fields correctly');
      }
    };

    const handlePrevious = () => {
      setCurrentStep(2);
      setError('');
    };

    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5" />
            Employment Information
          </CardTitle>
          <CardDescription>Provide your employment and income details. Fields marked with * are required.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="employment_type" className="flex items-center gap-1">
              Employment Type <span className="text-red-500">*</span>
            </Label>
            <Select 
              value={stepData.employment_type || ''} 
              onValueChange={(value) => setStepData({...stepData, employment_type: value})}
            >
              <SelectTrigger className={validationErrors.employment_type ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select employment type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full_time">Full-time Employee</SelectItem>
                <SelectItem value="part_time">Part-time Employee</SelectItem>
                <SelectItem value="self_employed">Self-employed</SelectItem>
                <SelectItem value="freelancer">Freelancer</SelectItem>
                <SelectItem value="unemployed">Unemployed</SelectItem>
              </SelectContent>
            </Select>
            {validationErrors.employment_type && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.employment_type}</p>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="employer_name" className="flex items-center gap-1">
                Employer Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="employer_name"
                value={stepData.employer_name || ''}
                onChange={(e) => setStepData({...stepData, employer_name: e.target.value})}
                placeholder="Enter employer name"
                className={validationErrors.employer_name ? 'border-red-500 focus:border-red-500' : ''}
                required
              />
              {validationErrors.employer_name && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.employer_name}</p>
              )}
            </div>
            <div>
              <Label htmlFor="job_title" className="flex items-center gap-1">
                Job Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="job_title"
                value={stepData.job_title || ''}
                onChange={(e) => setStepData({...stepData, job_title: e.target.value})}
                placeholder="Enter your job title"
                className={validationErrors.job_title ? 'border-red-500 focus:border-red-500' : ''}
                required
              />
              {validationErrors.job_title && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.job_title}</p>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="work_experience" className="flex items-center gap-1">
                Work Experience (years) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="work_experience"
                type="number"
                value={stepData.work_experience || ''}
                onChange={(e) => setStepData({...stepData, work_experience: parseInt(e.target.value)})}
                placeholder="Enter years of experience"
                className={validationErrors.work_experience ? 'border-red-500 focus:border-red-500' : ''}
                min="0"
                max="50"
                required
              />
              {validationErrors.work_experience && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.work_experience}</p>
              )}
            </div>
            <div>
              <Label htmlFor="monthly_income" className="flex items-center gap-1">
                Monthly Income ($) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="monthly_income"
                type="number"
                value={stepData.monthly_income || ''}
                onChange={(e) => setStepData({...stepData, monthly_income: parseFloat(e.target.value)})}
                placeholder="Enter monthly income"
                className={validationErrors.monthly_income ? 'border-red-500 focus:border-red-500' : ''}
                min="1"
                max="1000000"
                required
              />
              {validationErrors.monthly_income && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.monthly_income}</p>
              )}
            </div>
          </div>
          
          <div>
            <Label htmlFor="office_address" className="flex items-center gap-1">
              Office Address <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="office_address"
              value={stepData.office_address || ''}
              onChange={(e) => setStepData({...stepData, office_address: e.target.value})}
              placeholder="Enter your office address"
              className={validationErrors.office_address ? 'border-red-500 focus:border-red-500' : ''}
              required
            />
            {validationErrors.office_address && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.office_address}</p>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handlePrevious}
              className="flex-1"
            >
              Previous
            </Button>
            <Button 
              onClick={handleNext}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              Next Step
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const FinancialInfoStep = () => {
    const [stepData, setStepData] = useState(formData.financial_info);
    const [validationErrors, setValidationErrors] = useState({});

    const validateStep = () => {
      const errors = {};
      const requiredFields = [
        'monthly_expenses', 'credit_score', 'bank_account_details'
      ];

      requiredFields.forEach(field => {
        if (!stepData[field] || (typeof stepData[field] === 'string' && stepData[field].trim() === '')) {
          errors[field] = 'This field is required';
        }
      });

      // Monthly expenses validation
      if (stepData.monthly_expenses && (stepData.monthly_expenses < 0 || stepData.monthly_expenses > 500000)) {
        errors.monthly_expenses = 'Monthly expenses must be between $0 and $500,000';
      }

      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const handleNext = () => {
      if (validateStep()) {
        handleStepSubmit(stepData);
      } else {
        setError('Please fill in all required fields correctly');
      }
    };

    const handlePrevious = () => {
      setCurrentStep(3);
      setError('');
    };

    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Financial Information
          </CardTitle>
          <CardDescription>Provide your financial details for assessment. Fields marked with * are required.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="monthly_expenses" className="flex items-center gap-1">
              Monthly Expenses ($) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="monthly_expenses"
              type="number"
              value={stepData.monthly_expenses || ''}
              onChange={(e) => setStepData({...stepData, monthly_expenses: parseFloat(e.target.value)})}
              placeholder="Enter your monthly expenses"
              className={validationErrors.monthly_expenses ? 'border-red-500 focus:border-red-500' : ''}
              min="0"
              max="500000"
              required
            />
            {validationErrors.monthly_expenses && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.monthly_expenses}</p>
            )}
          </div>
          
          <div>
            <Label htmlFor="existing_loans">Existing Loans</Label>
            <Textarea
              id="existing_loans"
              value={stepData.existing_loans || ''}
              onChange={(e) => setStepData({...stepData, existing_loans: e.target.value})}
              placeholder="Describe any existing loans (if any). Enter 'None' if no existing loans."
            />
          </div>
          
          <div>
            <Label htmlFor="credit_score" className="flex items-center gap-1">
              Credit Score Range <span className="text-red-500">*</span>
            </Label>
            <Select 
              value={stepData.credit_score || ''} 
              onValueChange={(value) => setStepData({...stepData, credit_score: value})}
            >
              <SelectTrigger className={validationErrors.credit_score ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select your credit score range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="excellent">Excellent (750+)</SelectItem>
                <SelectItem value="good">Good (700-749)</SelectItem>
                <SelectItem value="fair">Fair (650-699)</SelectItem>
                <SelectItem value="poor">Poor (600-649)</SelectItem>
                <SelectItem value="very_poor">Very Poor (Below 600)</SelectItem>
                <SelectItem value="unknown">Don't Know</SelectItem>
              </SelectContent>
            </Select>
            {validationErrors.credit_score && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.credit_score}</p>
            )}
          </div>
          
          <div>
            <Label htmlFor="bank_account_details" className="flex items-center gap-1">
              Bank Account Details <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="bank_account_details"
              value={stepData.bank_account_details || ''}
              onChange={(e) => setStepData({...stepData, bank_account_details: e.target.value})}
              placeholder="Enter bank name and account type (e.g., Chase Bank - Checking Account)"
              className={validationErrors.bank_account_details ? 'border-red-500 focus:border-red-500' : ''}
              required
            />
            {validationErrors.bank_account_details && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.bank_account_details}</p>
            )}
          </div>
          
          <div>
            <Label htmlFor="other_income_sources">Other Income Sources</Label>
            <Textarea
              id="other_income_sources"
              value={stepData.other_income_sources || ''}
              onChange={(e) => setStepData({...stepData, other_income_sources: e.target.value})}
              placeholder="Any additional income sources (if any). Enter 'None' if no additional income."
            />
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handlePrevious}
              className="flex-1"
            >
              Previous
            </Button>
            <Button 
              onClick={handleNext}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              Next Step
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const DocumentUploadStep = () => {
    const [stepData, setStepData] = useState(formData.documents);
    const [validationErrors, setValidationErrors] = useState({});

    const handleFileUpload = (field, file) => {
      if (file) {
        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
          setValidationErrors(prev => ({
            ...prev,
            [field]: 'File size must be less than 10MB'
          }));
          return;
        }

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
          setValidationErrors(prev => ({
            ...prev,
            [field]: 'Only JPEG, PNG, and PDF files are allowed'
          }));
          return;
        }

        // Clear any previous errors for this field
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });

        const reader = new FileReader();
        reader.onload = (e) => {
          setStepData({...stepData, [field]: e.target.result});
        };
        reader.readAsDataURL(file);
      }
    };

    const validateStep = () => {
      const errors = {};
      const requiredDocuments = ['id_proof', 'income_proof'];

      requiredDocuments.forEach(field => {
        if (!stepData[field]) {
          errors[field] = 'This document is required';
        }
      });

      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const handleSubmit = () => {
      if (validateStep()) {
        handleStepSubmit(stepData);
      } else {
        setError('Please upload all required documents');
      }
    };

    const handlePrevious = () => {
      setCurrentStep(4);
      setError('');
    };

    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Document Upload
          </CardTitle>
          <CardDescription>Upload required documents for verification. Documents marked with * are required.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-gray-600 p-3 bg-blue-50 rounded-lg">
            <p><strong>File Requirements:</strong></p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Accepted formats: JPEG, PNG, PDF</li>
              <li>Maximum file size: 10MB per file</li>
              <li>Ensure documents are clear and legible</li>
            </ul>
          </div>

          <div>
            <Label htmlFor="id_proof" className="flex items-center gap-1">
              ID Proof (Driver's License, Passport, etc.) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="id_proof"
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => handleFileUpload('id_proof', e.target.files[0])}
              className={`mt-1 ${validationErrors.id_proof ? 'border-red-500' : ''}`}
            />
            {validationErrors.id_proof && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.id_proof}</p>
            )}
            {stepData.id_proof && !validationErrors.id_proof && (
              <Badge variant="secondary" className="mt-2 bg-green-100 text-green-800">✓ Uploaded Successfully</Badge>
            )}
          </div>
          
          <div>
            <Label htmlFor="income_proof" className="flex items-center gap-1">
              Income Proof (Pay stubs, Tax returns, etc.) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="income_proof"
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => handleFileUpload('income_proof', e.target.files[0])}
              className={`mt-1 ${validationErrors.income_proof ? 'border-red-500' : ''}`}
            />
            {validationErrors.income_proof && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.income_proof}</p>
            )}
            {stepData.income_proof && !validationErrors.income_proof && (
              <Badge variant="secondary" className="mt-2 bg-green-100 text-green-800">✓ Uploaded Successfully</Badge>
            )}
          </div>
          
          <div>
            <Label htmlFor="address_proof">Address Proof (Utility bills, Bank statements, etc.)</Label>
            <Input
              id="address_proof"
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => handleFileUpload('address_proof', e.target.files[0])}
              className={`mt-1 ${validationErrors.address_proof ? 'border-red-500' : ''}`}
            />
            {validationErrors.address_proof && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.address_proof}</p>
            )}
            {stepData.address_proof && !validationErrors.address_proof && (
              <Badge variant="secondary" className="mt-2 bg-green-100 text-green-800">✓ Uploaded Successfully</Badge>
            )}
            <p className="text-sm text-gray-500 mt-1">Optional - but recommended for faster processing</p>
          </div>
          
          <div>
            <Label htmlFor="bank_statements">Bank Statements (Last 3 months)</Label>
            <Input
              id="bank_statements"
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => handleFileUpload('bank_statements', e.target.files[0])}
              className={`mt-1 ${validationErrors.bank_statements ? 'border-red-500' : ''}`}
            />
            {validationErrors.bank_statements && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.bank_statements}</p>
            )}
            {stepData.bank_statements && !validationErrors.bank_statements && (
              <Badge variant="secondary" className="mt-2 bg-green-100 text-green-800">✓ Uploaded Successfully</Badge>
            )}
            <p className="text-sm text-gray-500 mt-1">Optional - but recommended for faster processing</p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handlePrevious}
              className="flex-1"
            >
              Previous
            </Button>
            <Button 
              onClick={handleSubmit}
              className="flex-1 bg-green-600 hover:bg-green-700"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Application'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Main Dashboard Component
  const Dashboard = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Loan Dashboard</h1>
            <p className="text-gray-600">Welcome back, {currentUser?.full_name}</p>
          </div>
          <div className="flex gap-2">
            {!showLoanForm && (
              <Button 
                onClick={() => setShowLoanForm(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                New Loan Application
              </Button>
            )}
            <Button variant="outline" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>

        {/* Success/Error Messages */}
        {error && (
          <Alert className="mb-4 border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-700">{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="mb-4 border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-700">{success}</AlertDescription>
          </Alert>
        )}

        {/* Loan Form */}
        {showLoanForm && (
          <div className="mb-8">
            <Card className="mb-4">
              <CardHeader>
                <CardTitle>New Loan Application</CardTitle>
                <CardDescription>Complete all steps to submit your application</CardDescription>
                <Progress value={(currentStep / 5) * 100} className="mt-2" />
                <div className="flex justify-between text-sm text-gray-500 mt-2">
                  <span>Step {currentStep} of 5</span>
                  <span>{Math.round((currentStep / 5) * 100)}% Complete</span>
                </div>
              </CardHeader>
            </Card>

            <div className="space-y-6">
              {currentStep === 1 && <PersonalInfoStep />}
              {currentStep === 2 && <LoanDetailsStep />}
              {currentStep === 3 && <EmploymentInfoStep />}
              {currentStep === 4 && <FinancialInfoStep />}
              {currentStep === 5 && <DocumentUploadStep />}
            </div>

            <div className="mt-4 text-center">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowLoanForm(false);
                  setCurrentStep(1);
                }}
              >
                Cancel Application
              </Button>
            </div>
          </div>
        )}

        {/* Applications List */}
        {!showLoanForm && (
          <Card>
            <CardHeader>
              <CardTitle>Your Loan Applications</CardTitle>
              <CardDescription>Track the status of your loan applications</CardDescription>
            </CardHeader>
            <CardContent>
              {userApplications.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No loan applications found</p>
                  <Button 
                    onClick={() => setShowLoanForm(true)}
                    className="mt-4 bg-blue-600 hover:bg-blue-700"
                  >
                    Apply for Your First Loan
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {userApplications.map((application) => (
                    <Card key={application.id} className="border-l-4 border-l-blue-500">
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">
                              {application.loan_details.loan_type.replace('_', ' ').toUpperCase()} LOAN
                            </CardTitle>
                            <CardDescription>
                              Applied on {new Date(application.created_at).toLocaleDateString()}
                            </CardDescription>
                          </div>
                          <Badge 
                            variant={application.status === 'submitted' ? 'secondary' : 'default'}
                            className="capitalize"
                          >
                            {application.status}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="font-medium">Loan Amount:</span>
                            <p>${application.loan_details.loan_amount?.toLocaleString()}</p>
                          </div>
                          <div>
                            <span className="font-medium">Tenure:</span>
                            <p>{application.loan_details.loan_tenure} months</p>
                          </div>
                          <div>
                            <span className="font-medium">Application ID:</span>
                            <p className="font-mono text-xs">{application.id}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );

  // Main render
  if (!currentUser) {
    return <AuthForm />;
  }

  return <Dashboard />;
}

export default App;
